import type { IconType, LinkType } from '@/frontend-types'
import { cva } from 'cva'
import Link from 'next/link'
import type { ComponentProps, FC } from 'react'
import { FontAwesomeIcon } from './FontAwesomeIcon'

export type LinkIconType = LinkType & {
	icon?: IconType
	intent: 'primary' | 'white'
}

export type LinkIconProps = Omit<ComponentProps<typeof Link>, 'children' | 'href'> &
	Omit<LinkIconType, 'linkId'>

export const linkIconVariants = cva({
	base: 'relative w-fit flex items-center gap-x-xs transition-colors duration-200 ease-in-out cursor-pointer group text-button',
	variants: {
		intent: {
			primary: 'text-blue hover:text-blue-shade-40 focus:text-blue-shade-70',
			white: 'text-white hover:text-blue-tint-20 focus:text-white',
		},
	},
})

export const linkIconAfterVariants = cva({
	base: 'relative after:absolute after:bottom-0 after:left-0 after:h-px after:w-0 after:transition-all after:duration-200 after:ease-in-out group-hover:after:w-full group-focus:after:w-full',
	variants: {
		intent: {
			primary:
				'after:bg-blue group-hover:after:bg-blue-shade-40 group-focus:after:bg-blue-shade-70',
			white: 'after:bg-white group-hover:after:bg-blue-tint-20 group-focus:after:bg-white',
		},
	},
})

export const LinkIcon: FC<LinkIconProps> = ({ icon, href, target, label, intent, ...props }) => {
	return (
		<Link target={target} className={linkIconVariants({ intent })} href={href} {...props}>
			<span className={linkIconAfterVariants({ intent })}>{label}</span>
			{icon && <FontAwesomeIcon icon={icon} size="md" />}
		</Link>
	)
}
